// @flow
import moment from 'moment';
import { get } from 'lodash';

import { UTC_DATE_TIME_FORMAT } from './apiDate';

function formatDate(outputFormat: string): (?string, ?string) => ?string {
  return (date: ?string, format: ?string) => {
    return date && format ? moment(date, format).format(outputFormat) : null;
  };
}

/**
 * Return an array of moment object for each days between
 * Opening and Closing dates (included).
 * @param  {string} opening The start date of range
 * @param  {string} closing The end date of range
 * @param  {string} format  The format of opening and closing date string
 * @return {array}          Array of moment objects
 */
function formatRangeDate(opening: string, closing: string, format: string): Array<moment> {
  const start = moment(opening, format);
  const end = moment(closing, format);
  const dateRange = [];
  let current = start;

  if (
    typeof opening === 'undefined' ||
    typeof closing === 'undefined' ||
    !(start instanceof moment) ||
    !(end instanceof moment) ||
    !start.isValid() ||
    !end.isValid() ||
    start.isAfter(end)
  ) {
    return dateRange;
  }

  dateRange.push(current);
  while (!current.isSame(end)) {
    current = current.clone().add(1, 'day');
    dateRange.push(current);
  }

  return dateRange;
}

function formatTimestampDate(date: ?timestamp, format: string): ?string {
  const momentDate = moment(date);

  return date && momentDate.isValid() ? momentDate.format(format) : null;
}

function getTimestamp(date: string, format: string): ?number {
  const momentDate = moment(date, format);

  return date && momentDate.isValid() ? momentDate.valueOf() : null;
}

function momentFromUTCDateTime(utcDateTime: string): moment {
  return moment(utcDateTime, UTC_DATE_TIME_FORMAT);
}

function isYearsRangeValid(
  departudeDate: string,
  format: string,
  add: moment$MomentDuration | {},
): boolean {
  return moment(departudeDate, format).isBetween([], moment().add(add));
}

/**
 * TODO
 * Add a "forcePositive" dates parameter
 * exemple :
 *    30 days after now with 30 days before now:
 *
 *      Math.abs(30) - Math.abs(-30) = 0
 *  OR
 *      Math.abs(-30) - Math.abs(30) = 0
 *
 *    "forcePositive" arg let the user to choose result.
 */
function closestDateSort(now: moment = moment(), format: string, property: ?string) {
  return (a: *, b: *) => {
    const valA = moment(property ? get(a, property) : a, format);
    const valB = moment(property ? get(b, property) : b, format);

    if (!valA.isValid() || !valB.isValid()) {
      return !valA.isValid() ? 1 : -1;
    }

    return Math.abs(now.diff(valA)) - Math.abs(now.diff(valB));
  };
}

export {
  closestDateSort,
  formatDate,
  formatRangeDate,
  formatTimestampDate,
  getTimestamp,
  isYearsRangeValid,
  momentFromUTCDateTime,
};
